import type { ReactNode } from 'react'
import { useAppStore, type NavEntry } from '../store/app'
import { useEdgeSwipeBack } from './useEdgeSwipeBack'
import { useMediaQuery, WIDE_LAYOUT_QUERY } from '../components/useMediaQuery'
import { AgentDetailScreen } from './agents/AgentDetailScreen'
import { AgentGrantsScreen } from './agents/AgentGrantsScreen'
import { AgentMandateScreen } from './agents/AgentMandateScreen'
import { AgentResourcesScreen } from './agents/AgentResourcesScreen'
import { AgentSettingsScreen } from './agents/AgentSettingsScreen'
import { NewAgentWizard } from './agents/NewAgentWizard'
import { OnboardingReviewScreen } from './agents/OnboardingReviewScreen'
import { ChatRoomScreen } from './chat/ChatRoomScreen'
import { RoutinesScreen } from './routines/RoutinesScreen'
import { RoutineEditorScreen } from './routines/RoutineEditorScreen'
import './NavStack.css'

// Hosts the pushed navigation stack above the root screen. Slide-in-right
// animation matches UINavigationController push. Top of navStack covers
// the whole viewport until popped. The top screen also takes the iOS-style
// left-edge swipe back to its parent (see useEdgeSwipeBack).
export function NavStack() {
  const navStack = useAppStore((s) => s.navStack)
  const popNav = useAppStore((s) => s.popNav)
  const wide = useMediaQuery(WIDE_LAYOUT_QUERY)
  if (navStack.length === 0) return null

  // Side panel (Slack's desktop layout): on a wide window, a thread opened
  // directly over its own room sits in a panel on the right while the room
  // stays usable on the left, instead of covering it. Only the LAYOUT changes —
  // the stack is still [..., room, thread], so Back, browser/Android back and
  // every "open this thread" path behave exactly as on a phone.
  const top = navStack[navStack.length - 1]
  const below = navStack[navStack.length - 2]
  const split =
    wide &&
    top?.kind === 'chat_thread' &&
    below?.kind === 'chat_room' &&
    below.roomID === top.roomID

  return (
    <>
      {navStack.map((entry, i) => {
        const layout: ScreenLayout = !split
          ? 'full'
          : i === navStack.length - 1
            ? 'split-pane'
            : i === navStack.length - 2
              ? 'split-main'
              : 'full'
        return (
          <NavScreen
            key={`${entry.kind}-${i}`}
            zIndex={150 + i}
            isTop={i === navStack.length - 1}
            layout={layout}
            onBack={popNav}
          >
            {renderEntry(entry, layout === 'split-pane')}
          </NavScreen>
        )
      })}
    </>
  )
}

// full       — covers the whole viewport (the phone layout, and every non-thread
//              screen on a wide one).
// split-main — the room, narrowed to leave room for its thread's panel.
// split-pane — the thread, as a panel on the right.
type ScreenLayout = 'full' | 'split-main' | 'split-pane'

function NavScreen({
  zIndex,
  isTop,
  layout,
  onBack,
  children,
}: {
  zIndex: number
  isTop: boolean
  layout: ScreenLayout
  onBack: () => void
  children: ReactNode
}) {
  const { style, handlers } = useEdgeSwipeBack(isTop, onBack)
  const cls = layout === 'full' ? '' : ` navstack__screen--${layout}`
  return (
    <div className={`navstack__screen${cls}`} style={{ zIndex, ...style }} {...handlers}>
      {children}
    </div>
  )
}

function renderEntry(entry: NavEntry, asPane: boolean) {
  return (
    <>
          {entry.kind === 'agent_detail' && (
            <AgentDetailScreen
              agentInstanceID={entry.agentInstanceID}
              positionID={entry.positionID}
            />
          )}
          {entry.kind === 'new_agent_wizard' && <NewAgentWizard />}
          {entry.kind === 'agent_mandate' && (
            <AgentMandateScreen
              title={entry.title}
              mandate={entry.mandate}
              jobDescriptionID={entry.jobDescriptionID}
              requiredSkills={entry.requiredSkills}
            />
          )}
          {entry.kind === 'agent_settings' && (
            <AgentSettingsScreen
              agentInstanceID={entry.agentInstanceID}
              positionID={entry.positionID}
              agentName={entry.agentName}
              hostname={entry.hostname}
            />
          )}
          {entry.kind === 'agent_grants' && (
            <AgentGrantsScreen positionID={entry.positionID} />
          )}
          {entry.kind === 'agent_resources' && (
            <AgentResourcesScreen agentInstanceID={entry.agentInstanceID} />
          )}
          {entry.kind === 'chat_room' && (
            <ChatRoomScreen roomID={entry.roomID} roomName={entry.roomName} />
          )}
          {entry.kind === 'chat_thread' && (
            <ChatRoomScreen
              roomID={entry.roomID}
              roomName={entry.roomName}
              threadRootID={entry.rootID}
              focusOnOpen={entry.focus}
              asPane={asPane}
            />
          )}
          {entry.kind === 'routines' && <RoutinesScreen />}
          {entry.kind === 'routine_editor' && (
            <RoutineEditorScreen triggerID={entry.triggerID} />
          )}
          {entry.kind === 'onboarding_review' && (
            <OnboardingReviewScreen
              proposalID={entry.proposalID}
              agentInstanceID={entry.agentInstanceID}
              positionID={entry.positionID}
            />
          )}
    </>
  )
}
